/**
 * VOS API Client — axios factory with request + response interceptors.
 *
 * Composes the pure error helpers from `apiErrors.js` (which are
 * separately unit-tested without axios) into a single source of truth
 * for HTTP calls. See `apiErrors.js` for the founder root-cause that
 * motivated this layer.
 *
 * Usage:
 *   const api = createApiClient({ token, onAuthFail });
 *   try { await api.get('/something'); }
 *   catch (e) { const fe = formatApiError(e); ... }
 */
import axios from 'axios';
import { isAuthError, isTokenExpired } from './apiErrors';

// Re-export the pure helpers so callers only need to import from one
// place (the existing `@/lib/apiClient` imports keep working).
export {
  isAuthError, isTokenExpired, formatApiError, tokenExpiresAt,
} from './apiErrors';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function createApiClient({ token, onAuthFail }) {
  const instance = axios.create({ baseURL: API_BASE });
  if (token) {
    instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
  // Request interceptor: short-circuit known-expired tokens so the
  // user sees the session-expired toast immediately instead of after
  // a wasted round-trip that ALSO confuses error banners.
  instance.interceptors.request.use(
    (config) => {
      if (token && isTokenExpired(token)) {
        const err = new Error('Token expired');
        err.response = { status: 401, data: { detail: 'Token expired' } };
        if (typeof onAuthFail === 'function') {
          queueMicrotask(onAuthFail);
        }
        return Promise.reject(err);
      }
      return config;
    },
    (err) => Promise.reject(err),
  );
  // Response interceptor: route 401s through the auth-fail handler
  // exactly once (debounced by the AuthContext).
  instance.interceptors.response.use(
    (r) => r,
    (err) => {
      if (isAuthError(err) && typeof onAuthFail === 'function') {
        queueMicrotask(onAuthFail);
      }
      return Promise.reject(err);
    },
  );
  return instance;
}
